import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Subject } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import { Organisation } from 'src/app/global/global.model';
import { ConfigAllService } from 'src/app/global/services/config-all/config-all.abstract.service';
import { PatchOrganisationConfiguration } from 'src/app/global/services/config-all/config-all.model';
import { BcitiContextService } from 'src/app/global/services/context/context.abstract.service';

@Component({
    selector: 'app-config-contact-info',
    templateUrl: './config-contact-info.component.html',
    styleUrls: ['./config-contact-info.component.scss'],
})
export class ConfigContactInfoComponent implements OnInit {
    destroy$ = new Subject();

    loading = false;
    processingSubmit = false;

    form = new FormGroup({
        website: new FormControl('', [this.optionalPatternValidator(/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([\/\w .-]*)*\/?$/)]),
    });
    optionalPatternValidator(pattern: RegExp) {
        return (control: FormControl) => {
            const value = control.value;
            if (!value) {
                return null;
            }
            return pattern.test(value) ? null : { pattern: true };
        };
    }

    org: Organisation;

    get website() {
        return this.form.get('website');
    }

    constructor(
        public translate: TranslateService,
        private configAllService: ConfigAllService,
        @Inject('BCITI_CONTEXT_SERVICE') private contextService: BcitiContextService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit() {
        this.contextService
            .getLanguage()
            .pipe(
                takeUntil(this.destroy$),
                tap((lang) => this.translate.use(lang)),
            )
            .subscribe();

        combineLatest([this.contextService.getOrganisationObs(), this.contextService.getOrganisationConfiguration()])
            .pipe(
                takeUntil(this.destroy$),
                map(([org, config]) => {
                    this.website.setValue(config.contactInformation?.website ?? '');

                    this.org = org;
                    this.cdr.detectChanges();
                    this.loading = false;
                }),
            )
            .subscribe();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    refreshOrgConfig() {
        this.contextService.refreshOrganisationConfiguration().pipe(takeUntil(this.destroy$)).subscribe();
    }

    public submit() {
        this.processingSubmit = true;
        const payload: PatchOrganisationConfiguration = {
            group: 'contactInformation',
            data: { website: this.website.value },
        };

        this.configAllService
            .patchOrganisationConfiguration(payload, this.org.tag)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (response) => {
                    this.processingSubmit = false;
                    this.contextService.successMessage(this.translate.instant('pages.config-all.config-contact.success'));
                    this.refreshOrgConfig();
                },
                (error) => {
                    this.processingSubmit = false;
                    this.contextService.errorMessage(this.translate.instant('pages.config-all.config-contact.error-message'));
                    console.error(error);
                },
            );
    }

    /**
     * Validate the form.
     */
    public validateForm() {
        return this.form.valid;
    }
}
